import { MeetingResponse } from 'api/meedioBackend/types';
import { getCurrentOrNextOccurrenceWindow, JOIN_PREROOM_THRESHOLD_MS } from 'utils/occurrenceWindow';

export interface GetMeetingElapsedStartOptions {
  now?: Date;
  /** Min active GroupCallMemberPrefix timestamp (ms) — video hub */
  liveSessionStartMs?: number | null;
  /** MESSENGER_CALL.started_at ISO — messenger sidebar */
  messengerCallStartedAt?: string | null;
}

export const getMeetingElapsedStartIso = (
  meeting: MeetingResponse | null,
  options: GetMeetingElapsedStartOptions = {}
): string | null => {
  const { now = new Date(), liveSessionStartMs, messengerCallStartedAt } = options;

  if (!meeting) return messengerCallStartedAt ?? null;

  const window = getCurrentOrNextOccurrenceWindow(meeting, now);
  const occurrenceStart =
    meeting.current_occurrence_scheduled_at ?? (window ? new Date(window.startMs).toISOString() : null);

  const nowMs = now.getTime();
  const occurrenceStartMs = occurrenceStart ? Date.parse(occurrenceStart) : null;
  const floorMs = occurrenceStartMs !== null ? occurrenceStartMs - JOIN_PREROOM_THRESHOLD_MS : Number.NEGATIVE_INFINITY;

  const activityMsCandidates: number[] = [];

  if (messengerCallStartedAt) {
    const ms = Date.parse(messengerCallStartedAt);
    if (Number.isFinite(ms)) activityMsCandidates.push(ms);
  }
  if (meeting.started_at) {
    const ms = Date.parse(meeting.started_at);
    if (Number.isFinite(ms)) activityMsCandidates.push(ms);
  }
  if (liveSessionStartMs != null && Number.isFinite(liveSessionStartMs)) {
    activityMsCandidates.push(liveSessionStartMs);
  }

  const activityCandidates = activityMsCandidates.filter((ms) => ms <= nowMs && ms >= floorMs);

  if (activityCandidates.length > 0) {
    return new Date(Math.min(...activityCandidates)).toISOString();
  }

  const fallbackCandidates = [occurrenceStart]
    .filter((v): v is string => !!v)
    .map((v) => Date.parse(v))
    .filter(Number.isFinite)
    .filter((ms) => ms <= nowMs);

  if (fallbackCandidates.length === 0) return null;
  return new Date(Math.min(...fallbackCandidates)).toISOString();
};
